// @flow
import type {Element} from 'react'
import React, {useCallback, useState, useEffect} from 'react'

import liftNodes from 'utils/GraphQL/typedLiftNodes'
import {createSummary} from 'components/proposals/Part/LinkedRole/summaryText'
import type {PickAlert, UpdateCallback} from 'components/proposals/types'
import type {AvailableRoles, Circle, OriginalRole, Organization}
  from 'components/proposals/Part/LinkedRole/types'
import SelectRole from './SelectRole'
import fetchAvailableRoles from './fetchAvailableRoles'
import type {fetchAvailableRolesQueryVariables as Variables}
  from './__generated__/fetchAvailableRolesQuery.graphql'

type Props = $ReadOnly<{
  availableRoles: AvailableRoles[],
  circle: Circle,
  organization: Organization,
  isReadonly: boolean,
  originalRole: ?OriginalRole,
  onChange: UpdateCallback,
  pickAlert: PickAlert,
}>

function CreateLinkedRoleEditor({
  availableRoles,
  circle,
  organization,
  isReadonly,
  originalRole,
  onChange,
  pickAlert,
}: Props): Element<"div"> {
  const [hasTouchedRole, setHasTouchedRole] = useState(false)
  const [allAvailableRoles, setAllAvailableRoles] = useState(availableRoles)

  const circleDatabaseId = circle.databaseId
  const orgDatabaseId = organization.databaseId

  useEffect(() => {
    const variables: Variables = {
      count: 5000,
      orgDatabaseId,
      circleDatabaseId,
    }

    const setFetchedAvailableRoles = async () => {
      const result = await fetchAvailableRoles(variables)
      const fetchedRoles = liftNodes(result)

      if (fetchedRoles)
        setAllAvailableRoles(fetchedRoles)
    }

    setFetchedAvailableRoles()
  }, [circleDatabaseId, orgDatabaseId])

  const roleAlert = hasTouchedRole ? pickAlert('originalRoleDatabaseId') : null

  const onRoleChange = useCallback((path: Array<number | string>, value: string) => {
    setHasTouchedRole(true)
    onChange(path, value)
  }, [setHasTouchedRole, onChange])

  return (
    <div>
      <div test-id="invite-role-selector">
        <SelectRole
          alert={roleAlert}
          availableRoles={allAvailableRoles}
          isReadonly={isReadonly}
          onChange={onRoleChange}
          path={['originalRole']}
          originalRole={originalRole}
        />
      </div>
      <span>
        {createSummary(originalRole, circle)}
      </span>
    </div>
  )
}

export default CreateLinkedRoleEditor
